/**
 * Modal "Dupliquer pour CS" : permet de choisir précisément quelles matières
 * de l'option FI seront dupliquées dans la nouvelle option CS.
 *
 * Les CS n'ont pas le même nombre de matières que les FI : cette modal liste
 * toutes les matières groupées par UE, toutes cochées par défaut. L'utilisateur
 * décoche celles qui ne sont PAS dispensées en CS.
 *
 * Les UE qui se retrouvent sans matière cochée ne sont pas créées.
 * Les classes (L1MI-CS…) sont créées pour les niveaux qui ont au moins une matière.
 *
 * Aucune note n'est touchée — opération de structure uniquement.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { X, Copy, Loader, Info, CheckSquare, Square } from 'lucide-react';
import apiClient from '../../config/api.config';

interface MatiereDetail {
  id: number;
  code_matiere: string;
  nom_matiere: string;
  niveau: string;
  semestre: number;
  credits: number;
}
interface UEDetail {
  id: number;
  code_ue: string;
  nom_ue: string;
  niveau: string;
  semestre: number;
  matieres: MatiereDetail[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  option: { id: number; code: string; nom: string } | null;
  onDone: (msg: string) => void;
}

const DupliquerPourCSModal: React.FC<Props> = ({ open, onClose, option, onDone }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ues, setUes] = useState<UEDetail[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [creerClasses, setCreerClasses] = useState(true);

  useEffect(() => {
    if (!open || !option) return;
    setLoading(true);
    setError(null);
    apiClient
      .get('/ues/', { params: { option: option.nom } })
      .then((resp) => {
        const data = (resp.data?.results || resp.data || []) as any[];
        const filtered = data.filter((u: any) => (u.option || '') === option.nom);
        const mapped: UEDetail[] = filtered.map((u: any) => ({
          id: u.id,
          code_ue: u.code_ue,
          nom_ue: u.nom_ue,
          niveau: u.niveau,
          semestre: u.semestre,
          matieres: (u.matieres || []).map((m: any) => ({
            id: m.id,
            code_matiere: m.code_matiere,
            nom_matiere: m.nom_matiere,
            niveau: m.niveau,
            semestre: m.semestre,
            credits: m.credits,
          })),
        }));
        mapped.sort((a, b) => {
          if (a.niveau !== b.niveau) return a.niveau.localeCompare(b.niveau);
          if (a.semestre !== b.semestre) return a.semestre - b.semestre;
          return (a.code_ue || '').localeCompare(b.code_ue || '');
        });
        setUes(mapped);
        // Toutes les matières cochées par défaut
        const allIds = new Set<number>();
        mapped.forEach((u) => u.matieres.forEach((m) => allIds.add(m.id)));
        setSelectedIds(allIds);
      })
      .catch((e: any) => {
        setError(e?.response?.data?.detail || e?.message || 'Erreur de chargement des UE/matières');
      })
      .finally(() => setLoading(false));
  }, [open, option]);

  const groupesParNiveau = useMemo(() => {
    const groupes: Record<string, UEDetail[]> = {};
    ues.forEach((u) => {
      const k = `${u.niveau} S${u.semestre}`;
      (groupes[k] = groupes[k] || []).push(u);
    });
    return groupes;
  }, [ues]);

  if (!open || !option) return null;

  const toggleMatiere = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleUE = (ue: UEDetail) => {
    const ids = ue.matieres.map((m) => m.id);
    const allChecked = ids.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allChecked) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleNiveau = (niveauKey: string) => {
    const niveauUes = groupesParNiveau[niveauKey] || [];
    const allIds: number[] = [];
    niveauUes.forEach((u) => u.matieres.forEach((m) => allIds.push(m.id)));
    const allChecked = allIds.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allChecked) allIds.forEach((id) => next.delete(id));
      else allIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const totalSelected = selectedIds.size;
  const totalMatieres = ues.reduce((sum, u) => sum + u.matieres.length, 0);

  const handleDupliquer = async () => {
    if (totalSelected === 0) {
      setError('Sélectionnez au moins une matière à dupliquer.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const resp = await apiClient.post(`/options/${option.id}/dupliquer-pour-cs/`, {
        matiere_ids: Array.from(selectedIds),
        creer_classes: creerClasses,
      });
      const a = resp.data?.actions || {};
      const det = [
        a.option_creee ? 'option créée' : 'option existait',
        `${a.matieres_crees} matières créées`,
        `${a.ues_creees} UE créées`,
        `${a.classes_creees} classes créées`,
      ].join(' · ');
      onDone(`${resp.data?.message || 'Duplication OK'} — ${det}`);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Erreur duplication CS');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Copy size={20} className="text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Dupliquer {option.nom} pour Formation Continue (CS)
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto">
          <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-sm text-blue-900 dark:text-blue-100">
            <Info size={16} className="flex-shrink-0 mt-0.5" />
            <div>
              Sélectionnez les matières dispensées en <strong>CS</strong>. Les autres ne seront pas dupliquées.
              <br />
              Code option cible : <code className="font-mono">{option.code}-CS</code> · Codes matières : <code className="font-mono">{'<code FI>'}-CS</code>
              <br />
              <strong>Aucune note n'est touchée</strong>. Opération idempotente (relancable).
            </div>
          </div>

          {loading && ues.length === 0 ? (
            <div className="text-center py-8">
              <Loader size={24} className="animate-spin inline-block text-blue-600" />
              <p className="text-sm text-gray-500 mt-2">Chargement des matières…</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 p-2 bg-gray-50 dark:bg-gray-900/40 rounded">
                <div className="text-sm">
                  <strong>{totalSelected}</strong> / {totalMatieres} matières sélectionnées
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const all = new Set<number>();
                      ues.forEach((u) => u.matieres.forEach((m) => all.add(m.id)));
                      setSelectedIds(all);
                    }}
                    className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300"
                  >
                    Tout cocher
                  </button>
                  <button
                    onClick={() => setSelectedIds(new Set())}
                    className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300"
                  >
                    Tout décocher
                  </button>
                </div>
              </div>

              {Object.entries(groupesParNiveau).map(([niveauKey, niveauUes]) => {
                const niveauIds: number[] = [];
                niveauUes.forEach((u) => u.matieres.forEach((m) => niveauIds.push(m.id)));
                const niveauChecked = niveauIds.length > 0 && niveauIds.every((id) => selectedIds.has(id));
                return (
                  <div key={niveauKey} className="border border-gray-200 dark:border-gray-700 rounded">
                    <div
                      className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-900/60 cursor-pointer"
                      onClick={() => toggleNiveau(niveauKey)}
                    >
                      {niveauChecked ? <CheckSquare size={16} className="text-blue-600" /> : <Square size={16} className="text-gray-400" />}
                      <strong className="text-sm">{niveauKey}</strong>
                      <span className="text-xs text-gray-500">({niveauIds.length} matières)</span>
                    </div>
                    {niveauUes.map((ue) => {
                      const ueIds = ue.matieres.map((m) => m.id);
                      const ueChecked = ueIds.length > 0 && ueIds.every((id) => selectedIds.has(id));
                      const ueSomeChecked = ueIds.some((id) => selectedIds.has(id));
                      return (
                        <div key={ue.id} className="border-t border-gray-200 dark:border-gray-700">
                          <div
                            className="flex items-center gap-2 px-4 py-1.5 bg-gray-50 dark:bg-gray-900/30 cursor-pointer"
                            onClick={() => toggleUE(ue)}
                          >
                            {ueChecked ? (
                              <CheckSquare size={14} className="text-blue-600" />
                            ) : ueSomeChecked ? (
                              <CheckSquare size={14} className="text-blue-300" />
                            ) : (
                              <Square size={14} className="text-gray-400" />
                            )}
                            <span className="text-xs font-mono text-gray-500">{ue.code_ue}</span>
                            <span className="text-sm">{ue.nom_ue}</span>
                          </div>
                          <div className="pl-8">
                            {ue.matieres.map((m) => (
                              <label
                                key={m.id}
                                className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer text-sm hover:bg-gray-50 dark:hover:bg-gray-700/40 ${
                                  selectedIds.has(m.id) ? '' : 'opacity-50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedIds.has(m.id)}
                                  onChange={() => toggleMatiere(m.id)}
                                  className="rounded"
                                />
                                <span className="text-xs font-mono text-gray-500">{m.code_matiere}</span>
                                <span className="flex-1">{m.nom_matiere}</span>
                                <span className="text-xs text-gray-400">{m.credits} cr.</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}

              <label className="flex items-center gap-2 p-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={creerClasses}
                  onChange={(e) => setCreerClasses(e.target.checked)}
                  className="rounded"
                />
                Créer aussi les classes (L1{option.code}-CS, L2{option.code}-CS…) pour les niveaux concernés
              </label>
            </>
          )}

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-900 dark:text-red-100">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 rounded-b-lg">
          <button onClick={onClose} disabled={loading} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            Annuler
          </button>
          <button
            onClick={handleDupliquer}
            disabled={loading || totalSelected === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm font-medium rounded"
          >
            {loading ? <Loader size={16} className="animate-spin" /> : <Copy size={16} />}
            Dupliquer ({totalSelected})
          </button>
        </div>
      </div>
    </div>
  );
};

export default DupliquerPourCSModal;
